import { LoadingStatus } from 'models/LoadingStatus';
import { useReducer, useEffect, Reducer, useCallback, useState } from 'react';
import { useDebounce, usePrevious } from 'hooks';
import { SEARCH_MINIMUM_LENGTH } from 'components/Search/actions';
import { useSelector } from 'react-redux';
import { PageState } from 'models/PageState';

interface OwnState {
  currentCol: number;
  currentRow: number;
  showResultView: boolean;
}

type Action =
  | { type: 'navigate'; currentCol: number; currentRow: number }
  | { type: 'reset' }
  | { type: 'toggleResult'; visible: boolean };

const initialState: OwnState = {
  currentCol: 0,
  currentRow: -1,
  showResultView: false
};

const ownReducer: Reducer<OwnState, Action> = (state, action) => {
  switch (action.type) {
    case 'navigate':
      return { ...state, currentCol: action.currentCol, currentRow: action.currentRow };
    case 'reset':
      return initialState;
    case 'toggleResult':
      return { ...state, showResultView: action.visible };
    default:
      return state;
  }
};

interface UseSearchOptions {
  searchFetchAction: (searchPhrase: string) => Promise<void>;
  loadingStatus: LoadingStatus;
}

const SEARCH_FETCH_DEBOUNCE_WAIT = 200;
const mapLocationSearch = (state: PageState) => state.location.search?.phrase || '';
const mapLoadingStatus = (state: PageState) => state.currentPage.status;

export const useSearch = ({ loadingStatus, searchFetchAction }: UseSearchOptions) => {
  const phrase = useSelector(mapLocationSearch);
  const pageStatus = useSelector(mapLoadingStatus);
  const prevPageStatus = usePrevious(pageStatus);

  useEffect(() => {
    // Re-set state phrase when a new page finished loading.
    /** History navigation sets location before fetching page, showing phrase
     *  before navigation is completed
     *  check page has started and completed loading */
    if (prevPageStatus !== LoadingStatus.Completed && pageStatus === LoadingStatus.Completed) {
      setSearchPhrase(decodeURI(phrase));
    }
  }, [phrase, pageStatus, prevPageStatus]);

  const [searchPhrase, setSearchPhrase] = useState(phrase);
  const [state, dispatch] = useReducer(ownReducer, initialState);

  const executeSearch = useCallback(
    (search: string) =>
      void searchFetchAction(search).then(() => {
        dispatch({ type: 'toggleResult', visible: true });
      }),
    [searchFetchAction]
  );

  const [searchFetch, cancelSearchFetch] = useDebounce(executeSearch, SEARCH_FETCH_DEBOUNCE_WAIT);

  useEffect(() => {
    void searchFetchAction('');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (searchPhrase.length >= SEARCH_MINIMUM_LENGTH) {
      searchFetch(searchPhrase);
    }

    if (searchPhrase.length < SEARCH_MINIMUM_LENGTH) {
      dispatch({ type: 'toggleResult', visible: false });
    }

    return cancelSearchFetch;
  }, [searchPhrase, searchFetch, cancelSearchFetch]);

  useEffect(() => {
    if (loadingStatus === LoadingStatus.Completed || !state.showResultView) {
      dispatch({ type: 'navigate', currentCol: 0, currentRow: -1 });
    }
  }, [loadingStatus, state.showResultView]);

  return {
    searchPhrase: searchPhrase,
    keypress: setSearchPhrase,
    navigateSearchResult: ({ nextCol, nextRow }: { nextCol: number; nextRow: number }) =>
      dispatch({ type: 'navigate', currentCol: nextCol, currentRow: nextRow }),
    reset: () => dispatch({ type: 'reset' }),
    ...state
  };
};
